/**
 * The API client.
 *
 * Same origin as the page, so there is no base URL to configure and no CORS.
 * Credentials ride on the session cookie — the browser never holds a bearer
 * token, which is why there is nothing here to store or clear.
 */
import type {
  ActivityItem,
  Agent,
  Channel,
  Claim,
  ConversationPage,
  DirectMessage,
  GithubConnection,
  Integration,
  MachineToken,
  Me,
  Member,
  Message,
  MessagePage,
  MintedToken,
  NotifyPrefs,
  Organisation,
  OrganisationSharing,
  PresenceSpan,
  ReadState,
  RepositoryRoom,
  RoomStatus,
  Run,
  RunTargets,
  SearchHit,
  SessionRow,
  Lesson,
  Setup,
  SetupSummary,
  SharedPublication,
  SharedSetup,
  StatusRevision,
  SignInMethods,
  UnreadPage,
} from './types'

/** A response the server understood and refused. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** A refusal's `detail` when it carried more than a sentence, else null. */
    readonly detail: unknown = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Not signed in — the signal to show the marketing site rather than log. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      // Only a string body is JSON. FormData has to set its own header so the
      // multipart boundary is right; naming the type here sends a body the
      // server cannot parse.
      ...(typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  if (!response.ok) {
    let detail = `request failed (${response.status})`
    let structured: unknown = null
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
      else if (typeof body?.detail?.message === 'string') {
        detail = body.detail.message
        structured = body.detail
      }
    } catch {
      // A non-JSON error body is not worth a second failure.
    }
    throw new ApiError(detail, response.status, structured)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const send = (method: string, path: string, body?: unknown) =>
  request<never>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

export const api = {
  // --- who you are ---------------------------------------------------------
  session: () => request<Me>('/v1/auth/session'),
  signInMethods: () => request<SignInMethods>('/v1/auth/methods'),
  signup: (payload: {
    email: string
    password: string
    display_name?: string
    organisation_name?: string
  }) => request<Me>('/v1/auth/signup', { method: 'POST', body: JSON.stringify(payload) }),
  login: (payload: { email: string; password: string }) =>
    request<Me>('/v1/auth/login', { method: 'POST', body: JSON.stringify(payload) }),
  logout: () => send('POST', '/v1/auth/logout'),
  /**
   * Ask for a password link.
   *
   * Always resolves the same way for a known and an unknown address — the
   * server answers 204 either way, on purpose, so this cannot be used to find
   * out which addresses have accounts.
   */
  forgotPassword: (payload: { email: string }): Promise<void> =>
    send('POST', '/v1/auth/password/forgot', payload),
  /** Whose account a password link belongs to. 404 when it is spent or stale. */
  passwordTokenEmail: (payload: { token: string }) =>
    request<{ email: string }>('/v1/auth/password/token', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Spend a password link. Signs you in, and ends every other session. */
  resetPassword: (payload: { token: string; password: string }) =>
    request<Me>('/v1/auth/password/reset', { method: 'POST', body: JSON.stringify(payload) }),

  me: () => request<Me>('/v1/me'),
  /**
   * `avatar_key: null` is how a face is removed; leaving it out keeps it.
   * `default_organisation: ''` is how a default is given up.
   */
  updateMe: (payload: {
    display_name?: string
    notify?: NotifyPrefs
    avatar_key?: string | null
    /** A slug to adopt, or `''` to stop having one. */
    default_organisation?: string
  }) => request<Me>('/v1/me', { method: 'PUT', body: JSON.stringify(payload) }),

  /**
   * Set a first password, or change the one there is.
   *
   * `current_password` is what the server refuses without when the account
   * already has one — a session left open on a borrowed machine should not be
   * enough to lock its owner out.
   */
  setPassword: (payload: { current_password?: string; new_password: string }) =>
    request<Me>('/v1/me/password', { method: 'PUT', body: JSON.stringify(payload) }),

  /** Refused while GitHub is the only way into the account. */
  unlinkGithub: () => request<Me>('/v1/me/github', { method: 'DELETE' }),
  /** Refused while Google is the only way into the account. */
  unlinkGoogle: () => request<Me>('/v1/me/google', { method: 'DELETE' }),

  // --- rooms ---------------------------------------------------------------
  organisations: () => request<Organisation[]>('/v1/organisations'),
  createOrganisation: (name: string) =>
    request<Organisation>('/v1/organisations', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  channels: (organisation: string) =>
    request<Channel[]>(`/v1/organisations/${organisation}/channels`),

  messages: (channelId: string, options: { since?: string; limit?: number; threaded?: boolean } = {}) => {
    const query = new URLSearchParams()
    if (options.since) query.set('since', options.since)
    if (options.limit) query.set('limit', String(options.limit))
    if (options.threaded) query.set('threaded', 'true')
    const suffix = query.toString() ? `?${query}` : ''
    return request<MessagePage>(`/v1/channels/${channelId}/messages${suffix}`)
  },
  /**
   * React to a message, as whoever is signed in.
   *
   * A put rather than a toggle so retrying is safe, which is what the agents
   * need; the caller decides which verb from whether it already holds one.
   */
  react: (channelId: string, messageId: string, reaction: string) =>
    request<Message>(
      `/v1/channels/${channelId}/messages/${encodeURIComponent(messageId)}`
      + `/reactions/${reaction}`, { method: 'PUT' }),
  unreact: (channelId: string, messageId: string, reaction: string) =>
    request<Message>(
      `/v1/channels/${channelId}/messages/${encodeURIComponent(messageId)}`
      + `/reactions/${reaction}`, { method: 'DELETE' }),
  post: (channelId: string, payload: { body: string; reply_to?: string | null; also_in_room?: boolean }) =>
    request<Message>(`/v1/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /**
   * Search this room's messages. Headlines only, never bodies: a hit names
   * the message to go to, it does not stand in for it.
   */
  search: (channelId: string, query: string, limit?: number) => {
    const params = new URLSearchParams({ query })
    if (limit) params.set('limit', String(limit))
    return request<SearchHit[]>(`/v1/channels/${channelId}/messages/search?${params}`)
  },

  agents: (channelId: string) => request<Agent[]>(`/v1/channels/${channelId}/agents`),
  sessions: (channelId: string) => request<SessionRow[]>(`/v1/channels/${channelId}/sessions`),
  /** Every session the room has kept, oldest first, stopped or running. */
  presence: (channelId: string) => request<PresenceSpan[]>(`/v1/channels/${channelId}/presence`),

  claims: (channelId: string) => request<Claim[]>(`/v1/channels/${channelId}/claims`),

  status: (channelId: string) =>
    request<RoomStatus>(`/v1/channels/${channelId}/status`),
  setStatus: (
    channelId: string,
    sections: { now?: string; blocked?: string; up_next?: string },
  ) =>
    request<RoomStatus>(`/v1/channels/${channelId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sections),
    }),
  statusHistory: (channelId: string, section: string) =>
    request<StatusRevision[]>(
      `/v1/channels/${channelId}/status/${section}/history`,
    ),
  revertStatus: (channelId: string, section: string, revisionId: string) =>
    request<RoomStatus>(`/v1/channels/${channelId}/status/${section}/revert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_id: revisionId }),
    }),
  /** Your machines that could start an agent in this room, or why none can. */
  runTargets: (channelId: string) =>
    request<RunTargets>(`/v1/channels/${channelId}/run-targets`),
  /** 409 with a sentence when the machine went offline or stopped serving the room. */
  startRun: (channelId: string, run: {
    task: string
    machine_id?: string
    /** The private message that proposed this run, which starting it answers with Start. */
    proposal?: { agent: string; id: string }
  }) =>
    request<Run>(`/v1/channels/${channelId}/runs`, { method: 'POST', body: JSON.stringify(run) }),
  /** Everything up to `lastMessageId` read, and the seen rows under it gone. */
  markRead: (channelId: string, lastMessageId: string) =>
    send('PUT', `/v1/channels/${channelId}/read`, { last_message_id: lastMessageId }),
  /** How far you have read the room: the floor, and what you have seen above it. */
  seen: (channelId: string) => request<ReadState>(`/v1/channels/${channelId}/seen`),
  /** 1 to 200 messages you have seen; the floor moves up over every run of them. */
  markSeen: (channelId: string, messageIds: string[]) =>
    request<ReadState>(`/v1/channels/${channelId}/seen`, {
      method: 'PUT',
      body: JSON.stringify({ message_ids: messageIds }),
    }),

  // --- direct messages: the agent is a query or body field, since its name holds slashes
  /** Everything between you and your agent, oldest first; `before` is the page before. */
  conversation: (organisation: string, agent: string, before?: string | null) => {
    const query = new URLSearchParams({ agent })
    if (before) query.set('before', before)
    return request<ConversationPage>(`/v1/organisations/${organisation}/conversation?${query}`)
  },
  /** With `also_post`, the room of the agent's newest live session gets a copy. */
  postDirect: (organisation: string, message: {
    agent: string; body: string; sender_type: 'human'; also_post: boolean; reply_to?: string | null
  }) => request<DirectMessage>(`/v1/organisations/${organisation}/direct`, {
    method: 'POST',
    body: JSON.stringify(message),
  }),
  markDirectRead: (organisation: string, agent: string, cursor: string) =>
    send('PUT', `/v1/organisations/${organisation}/direct/read`, { agent, cursor }),

  /** What you have not read in the organisation, a group per place, each its newest `perPlace` messages. */
  unread: (organisation: string, perPlace?: number) =>
    request<UnreadPage>(`/v1/organisations/${organisation}/unread${perPlace ? `?per_place=${perPlace}` : ''}`),

  activity: (organisation?: string) =>
    request<ActivityItem[]>(
      `/v1/activity${organisation ? `?organisation=${organisation}` : ''}`,
    ),

  // --- settings ------------------------------------------------------------
  members: (organisation: string) =>
    request<Member[]>(`/v1/organisations/${organisation}/members`),
  invite: (organisation: string, email: string) =>
    request<Member>(`/v1/organisations/${organisation}/members`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  removeMember: (organisation: string, memberId: string) =>
    request<void>(`/v1/organisations/${organisation}/members/${memberId}`,
      { method: 'DELETE' }),
  reinviteMember: (organisation: string, memberId: string) =>
    request<Member>(`/v1/organisations/${organisation}/members/${memberId}/reinvite`, {
      method: 'POST',
    }),
  memberLoginLink: (organisation: string, memberId: string) =>
    request<{ link: string }>(
      `/v1/organisations/${organisation}/members/${memberId}/login-link`,
      { method: 'POST' },
    ),
  updateOrganisation: (
    organisation: string,
    payload: { name?: string; retention_days?: number; face_style?: string },
  ) =>
    request<Organisation>(`/v1/organisations/${organisation}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  githubConnection: (organisation: string) =>
    request<GithubConnection>(`/v1/organisations/${organisation}/github`),
  setRepositoryMute: (organisation: string,
                      payload: { repository_key: string; enabled: boolean }) =>
    request<RepositoryRoom>(`/v1/organisations/${organisation}/github`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  disconnectInstallation: (organisation: string, installationId: number) =>
    request<void>(
      `/v1/organisations/${organisation}/github/installations/${installationId}`,
      { method: 'DELETE' }),

  /**
   * Every agent in the organisation, across all of its rooms.
   *
   * The Faces pane asks for this rather than the room roster: a face is unique
   * per organisation, so the pool being chosen from is the organisation's.
   */
  organisationAgents: (organisation: string) =>
    request<Agent[]>(`/v1/organisations/${organisation}/agents`),

  updateAgent: (
    organisation: string,
    name: string,
    payload: { role?: string; biography?: string; summary?: string; avatar_key?: string },
  ) =>
    request<Agent>(`/v1/organisations/${organisation}/agents/${name}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  /**
   * Store a portrait and get back the key to attach to an agent.
   *
   * No Content-Type header on purpose — the browser has to set the multipart
   * boundary itself, and naming the type here would send a body the server
   * cannot parse.
   */
  uploadMedia: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<{ key: string; url: string }>('/v1/media', { method: 'POST', body: form })
  },

  tokens: () => request<MachineToken[]>('/v1/tokens'),
  mintToken: (machineName: string) =>
    request<MintedToken>('/v1/tokens', {
      method: 'POST',
      body: JSON.stringify({ machine_name: machineName }),
    }),
  revokeToken: (id: string) => send('DELETE', `/v1/tokens/${id}`),
  /** Owner-only on the server; the pane asks only when the viewer owns. */
  integrations: (organisation: string) =>
    request<Integration[]>(`/v1/organisations/${organisation}/integrations`),
  revokeIntegration: (organisation: string, id: string) =>
    request<void>(`/v1/organisations/${organisation}/integrations/${id}`, { method: 'DELETE' }),

  // --- lessons -------------------------------------------------------------
  /** Every rule still standing. Superseded ones are excluded, never deleted. */
  lessons: (organisation: string) =>
    request<Lesson[]>(`/v1/organisations/${encodeURIComponent(organisation)}/lessons`),
  /**
   * The rules injected into every session regardless of task.
   *
   * Its own call rather than a filter over the listing above, because it is
   * what the hook fetches and it should not read the whole corpus to find
   * twenty rows. The pane asks for the same thing the agents get.
   */
  coreLessons: (organisation: string) =>
    request<Lesson[]>(`/v1/organisations/${encodeURIComponent(organisation)}/lessons/core`),
  /** One lesson, superseded or not, which is how the pane walks a supersede chain. */
  lesson: (organisation: string, lessonId: string) =>
    request<Lesson>(
      `/v1/organisations/${encodeURIComponent(organisation)}/lessons/${lessonId}`),
  /**
   * The lessons nearest a described task.
   *
   * A POST, and not because anything is written. The task text describes what
   * somebody is working on, and a query string puts that in every access log
   * between here and the box.
   *
   * Answers an empty list on a server with no embedding key. That is a
   * configuration, not a failure, and the pane says so rather than erroring.
   */
  searchLessons: (organisation: string, task: string, limit = 8) =>
    request<Lesson[]>(
      `/v1/organisations/${encodeURIComponent(organisation)}/lessons/search`, {
        method: 'POST',
        body: JSON.stringify({ task, limit }),
      }),
  /**
   * Put a rule in the always-injected core, or take it out. Owner only.
   *
   * The one owner-gated action on a lesson, and the line is the one already
   * written down for org-scoped settings: reversibility. Writing a lesson is
   * undone by writing another. This changes what every session in the
   * organisation reads before it does anything.
   */
  setLessonAlways: (organisation: string, lessonId: string, always: boolean) =>
    request<Lesson>(
      `/v1/organisations/${encodeURIComponent(organisation)}/lessons/${lessonId}/always`, {
        method: 'PUT',
        body: JSON.stringify({ always }),
      }),

  // --- setup records -------------------------------------------------------
  /** Your own record, unpublished items and all. Nobody else gets those. */
  mySetup: () => request<Setup>('/v1/me/setup'),
  /**
   * Withdraw one published item.
   *
   * There is no publish here, and that is deliberate. Publishing uploads a
   * tree of files, so it can only come from a machine that holds them;
   * withdrawing must not need that machine to still exist, which is why this
   * half is a browser call and the other half is not.
   */
  unpublishItem: (kind: string, name: string): Promise<void> =>
    send('DELETE',
         `/v1/me/setup/publications/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`),
  /**
   * Forget one machine, addressed by its identity, or by its label when it was
   * recorded before there were identities and so has none.
   *
   * A browser call rather than a command, and that follows from a rule already
   * here: this erases, so the endpoint takes the session cookie, while a
   * command line holds a machine token that lives in configuration files.
   *
   * A label stopped being unique once machines carried identities, so a label
   * naming two rows is refused with 409 and the identities to pick from.
   */
  forgetMachine: (machine: string): Promise<void> =>
    send('DELETE', `/v1/me/machines/${encodeURIComponent(machine)}`),
  /** Where a run starts when nobody names a machine. Null clears it. */
  setDefaultMachine: (machineId: string | null): Promise<void> =>
    send('PUT', '/v1/me/default-machine', { machine_id: machineId }),
  /** The mode agents start in on one machine. Null leaves it to the runner. */
  setPermissionMode: (machineId: string, permissionMode: string | null): Promise<void> =>
    send('PUT', `/v1/me/machines/${encodeURIComponent(machineId)}/permission-mode`,
      { permission_mode: permissionMode }),
  /**
   * Whether one organisation may read your record at all.
   *
   * Per organisation, and narrowing rather than widening: an item aimed at an
   * organisation whose switch is off is invisible however its audience reads.
   */
  setSetupSharing: (organisation: string, enabled: boolean) =>
    request<OrganisationSharing>(
      `/v1/organisations/${encodeURIComponent(organisation)}/setup-sharing`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      }),
  /**
   * Say which organisations read one publication.
   *
   * The whole set every time, so removing one is the same call as adding one.
   * The path puts the thing being set before the name, because `{name:path}`
   * on the server matches the rest of the path and would swallow a suffix.
   */
  setPublicationAudience: (kind: string, name: string, organisations: string[]) =>
    request<{ kind: string; name: string; audience: string[] }>(
      `/v1/me/setup/audiences/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`, {
        method: 'PUT',
        body: JSON.stringify({ organisations }),
      }),
  /** Mint a link, or replace the one there is. Rotating is the same act. */
  mintShareLink: (kind: string, name: string) =>
    request<{ kind: string; name: string; url: string }>(
      `/v1/me/setup/links/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`,
      { method: 'POST' }),
  /** Take the link away. Every copy of it stops working at once. */
  revokeShareLink: (kind: string, name: string): Promise<void> =>
    send('DELETE',
         `/v1/me/setup/links/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`),
  /** One publication, for somebody holding its link and no account at all. */
  sharedPublication: (token: string) =>
    request<SharedPublication>(`/v1/shared/${encodeURIComponent(token)}`),
  /**
   * Mint the record's own link, or replace the one there is.
   *
   * Beside the per-item link and not folded into it: that one names one item,
   * this one names a setup, and a person sending the first does not mean the
   * second.
   */
  mintSetupLink: () =>
    request<{ url: string }>('/v1/me/setup/link', { method: 'POST' }),
  /** Take the record link away. Every copy of it stops working at once. */
  revokeSetupLink: (): Promise<void> => send('DELETE', '/v1/me/setup/link'),
  /**
   * Say which publications the record link carries.
   *
   * The whole set every time, so removing one is the same request as adding
   * one. The pane re-reads the record after every write, so it always has the
   * full set to send.
   */
  setSetupLinkItems: (references: string[]) =>
    request<{ references: string[] }>('/v1/me/setup/link/items', {
      method: 'PUT',
      body: JSON.stringify({ references }),
    }),
  /** One person's carried setup, for somebody holding the link and no account. */
  sharedSetup: (token: string) =>
    request<SharedSetup>(`/v1/shared/setups/${encodeURIComponent(token)}`),
  /** A zip of your own setup, a plain GET the session cookie rides on, so a link rather than a fetch. */
  setupExportUrl: () => '/v1/me/setup/export',
  /** The same zip for a carried setup, open to whoever holds the token. */
  sharedSetupExportUrl: (token: string) =>
    `/v1/shared/setups/${encodeURIComponent(token)}/export`,
  /** Who in this organisation shares. Anyone with it switched off is absent. */
  setups: (organisation: string) =>
    request<SetupSummary[]>(`/v1/organisations/${organisation}/setups`),
  /** One person's record: what has an origin, plus whatever they published. */
  memberSetup: (organisation: string, member: string) =>
    request<Setup>(
      `/v1/organisations/${organisation}/setups/${encodeURIComponent(member)}`),

  // --- the danger zone -----------------------------------------------------
  /** Erase an organisation: rooms, messages, agents, memberships. Owner only. */
  deleteOrganisation: (organisation: string) =>
    send('DELETE', `/v1/organisations/${organisation}`),
  /** Erase your account, and the organisations nobody else was in. */
  deleteAccount: () => send('DELETE', '/v1/me'),
}

/** Every path the client knows how to call. Used by the contract test. */
export const KNOWN_PATHS: readonly string[] = [
  '/v1/auth/session', '/v1/auth/signup', '/v1/auth/login', '/v1/auth/logout',
  '/v1/auth/methods',
  '/v1/auth/password/forgot', '/v1/auth/password/token', '/v1/auth/password/reset',
  '/v1/me', '/v1/me/password', '/v1/me/github', '/v1/me/google',
  '/v1/organisations', '/v1/organisations/{organisation}/channels',
  '/v1/channels/{channel_id}/messages', '/v1/channels/{channel_id}/threads/{root_id}',
  '/v1/channels/{channel_id}/messages/search',
  '/v1/channels/{channel_id}/agents', '/v1/channels/{channel_id}/sessions',
  '/v1/channels/{channel_id}/presence',
  '/v1/channels/{channel_id}/read', '/v1/channels/{channel_id}/events',
  '/v1/channels/{channel_id}/plan',
  '/v1/channels/{channel_id}/status',
  '/v1/channels/{channel_id}/status/{section}/history',
  '/v1/channels/{channel_id}/status/{section}/revert',
  '/v1/channels/{channel_id}/run-targets', '/v1/channels/{channel_id}/runs',
  '/v1/activity', '/v1/organisations/{organisation}/members',
  '/v1/organisations/{organisation}', '/v1/organisations/{organisation}/github',
  '/v1/organisations/{organisation}/github/installations/{installation_id}',
  '/v1/organisations/{organisation}/agents',
  '/v1/organisations/{organisation}/lessons',
  '/v1/organisations/{organisation}/lessons/core',
  '/v1/organisations/{organisation}/lessons/search',
  '/v1/organisations/{organisation}/lessons/propose',
  '/v1/organisations/{organisation}/lessons/{lesson_id}',
  '/v1/organisations/{organisation}/lessons/{lesson_id}/always',
  '/v1/organisations/{organisation}/agents/{name}',
  '/v1/media',
  '/v1/tokens', '/v1/tokens/{token_id}',
  '/v1/organisations/{organisation}/integrations',
  '/v1/organisations/{organisation}/integrations/{grant_id}',
  '/v1/me/machines/{machine}', '/v1/me/default-machine',
  '/v1/me/machines/{machine_id}/permission-mode',
  '/v1/me/setup', '/v1/organisations/{organisation}/setup-sharing',
  '/v1/me/setup/publications/{kind}/{name}',
  '/v1/me/setup/audiences/{kind}/{name}', '/v1/me/setup/links/{kind}/{name}',
  '/v1/shared/{token}',
  '/v1/organisations/{organisation}/setups',
  '/v1/organisations/{organisation}/setups/{member}',
  '/v1/organisations/{organisation}/direct', '/v1/organisations/{organisation}/direct/read',
  '/v1/organisations/{organisation}/conversation',
]
